import { FC, useMemo } from "react";
import { useFlag } from "@unleash/proxy-client-react";
import { FilterValueInput } from "./components/FilterValueInput";
import { FilterValueAutocomplete } from "./components/FilterValueAutocomplete";
import { FilterValueByKeyAutocomplete } from "./components/FilterValueByKeyAutocomplete";
import { FilterValueSingleSelect } from "./components/FilterValueSingleSelect";
import { FilterValueAutocompleteWithAsyncSearch } from "./components/FilterValueAutocompleteWithAsyncSearch";
import { getFilterValueDataTypeByOperator } from "../../../../../../utils/helpers/data-type-checkers/getFilterValueDataTypeByOperator";
import { filterValueIsSingleSelectByOperator } from "../../../../../../utils/helpers/data-type-checkers/filterValueIsSingleSelectByOperator";
import { FilterItemNodeWithId } from "../../../../../../utils/types/common";
import { filterAvailableFieldsChecker } from "../../../../../../utils/helpers/data-type-checkers/filterAvailableFieldsChecker";
import { useFilterTreeContext } from "../../../../FilterTreeProvider";
import {
  filterTypeWithAsyncSearch,
  filterTypeWithBatchValues,
} from "../../../../../../utils/constants/common";
import { KeyValuePair, PossibleFilterValues } from "@/services/cloudchipr.api";
import { isStringProviderType } from "@/utils/helpers/providers/isStringProviderType";
import { getProviderName } from "@/utils/helpers/providers/getProviderName";
import { FilterValueWithBatchInput } from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/FilterValueWithBatchInput";

interface FilterValueProps {
  label?: string;
  inBatchInput?: boolean;
  filter: FilterItemNodeWithId;
  onChange(value: FilterItemNodeWithId["value"]): void;
  dataFormater?(data: PossibleFilterValues): PossibleFilterValues;
}

export const FilterValue: FC<FilterValueProps> = ({
  filter,
  label,
  onChange,
  dataFormater,
  inBatchInput,
}) => {
  const { possibleFilters } = useFilterTreeContext();
  const enableBatchFilterValuesInput = useFlag("EnableBatchFilterValuesInput");

  const { hasKey } = filterAvailableFieldsChecker(filter.type, filter.operator);
  const valueType = getFilterValueDataTypeByOperator(filter.operator);
  const filterValue = hasKey
    ? (filter.value as KeyValuePair)?.value
    : filter.value;

  const valueIsPrimitive = valueType === "string";

  const withBatchInput =
    enableBatchFilterValuesInput &&
    !inBatchInput &&
    ["in", "not_in"].includes(filter.operator) &&
    filterTypeWithBatchValues.has(filter.type);

  const filterLabel = useMemo(() => {
    if (label) {
      return label;
    }

    let filterLabel = possibleFilters?.find(
      (pf) => pf.key === filter.type,
    )?.label;

    if (
      filter.type === "account" &&
      isStringProviderType(filter.filter_provider)
    ) {
      filterLabel = getProviderName(filter.filter_provider, {
        capitalize: true,
      });
    }

    return filterLabel ?? filter.type;
  }, [label, possibleFilters, filter.type, filter.filter_provider]);

  if (withBatchInput) {
    return (
      <FilterValueWithBatchInput
        filter={filter}
        onChange={onChange}
        valueHasKey={hasKey}
      />
    );
  }

  if (valueIsPrimitive) {
    if (filterValueIsSingleSelectByOperator(filter.operator)) {
      return (
        <FilterValueSingleSelect
          onChange={onChange}
          filterValue={filterValue as string}
          filterType={filter.type}
          filterLabel={filterLabel}
          provider={filter.filter_provider}
          dataFormater={dataFormater}
        />
      );
    } else {
      return (
        <FilterValueInput
          filterLabel={filterLabel}
          onChange={onChange}
          filter={filter}
        />
      );
    }
  }

  if (filterTypeWithAsyncSearch.has(filter.type)) {
    return (
      <FilterValueAutocompleteWithAsyncSearch
        onChange={onChange}
        filterLabel={filterLabel}
        filterValue={filterValue as string[]}
        filterType={filter.type}
        provider={filter.filter_provider}
        dataFormater={dataFormater}
      />
    );
  }

  if (hasKey) {
    return (
      <FilterValueByKeyAutocomplete
        onChange={onChange}
        filterLabel={filterLabel}
        filterType={filter.type}
        provider={filter.filter_provider}
        value={filter?.value as KeyValuePair}
        dataFormater={dataFormater}
      />
    );
  }

  return (
    <FilterValueAutocomplete
      onChange={onChange}
      filterLabel={filterLabel}
      filterValue={filterValue as string[]}
      filterType={filter.type}
      provider={filter.filter_provider}
      dataFormater={dataFormater}
    />
  );
};
